import { useEffect, useRef, useState } from "react";

import apiClient from "../../../services/apiClient";
import "./ParentTelegramPage.css";

// 학원 관리 > 텔레그램 · 출결(2026-09-30). 학부모 텔레그램(@gindaily_bot) 연결 현황, 문구, 출결 화면.
// 텔레그램을 연결한 집은 알림(알림·알림장)을 텔레그램으로만 받고, 연결하지 않은 집은 알림톡으로 받는다.
// 이은 집은 알림톡 대신 텔레그램으로 받는다. 텔레그램 문구는 카카오 심사가 없어 여기서 바로 고친다.
// 서버: api/echo/parent_telegram.py, 출결: api/echo/attendance_views.py

export const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    window.prompt("아래 글을 복사해 주세요", text);
  }
};

// 보낼 때와 똑같이: 아는 자리는 예시 값으로 채우고, 채울 수 없는 자리가 남은 줄은 뺀다
export const fillPreview = (text, variableList) => {
  let result = text;
  variableList.forEach((one) => {
    result = result.split(`{${one.key}}`).join(one.example);
  });
  return result
    .split("\n")
    .filter((line) => !/\{[가-힣A-Za-z]+\}/.test(line))
    .join("\n");
};

const unknownVariableList = (text, variableList) => {
  const known = new Set(variableList.map((one) => one.key));
  return [...new Set((text.match(/\{([가-힣A-Za-z]+)\}/g) || []).map((one) => one.slice(1, -1)))].filter(
    (key) => !known.has(key)
  );
};

export const VariableChips = ({ variableList, onInsert }) => (
  <div className="parent-tg__chips">
    {variableList.map((one) => (
      <button key={one.key} type="button" title={`예: ${one.example}`} onClick={() => onInsert(`{${one.key}}`)}>
        {`{${one.key}}`} <span>{one.label}</span>
      </button>
    ))}
  </div>
);

// 커서 자리에 {학생명} 같은 자리를 끼워 넣는다
export const insertAtCursor = (textarea, value, current) => {
  if (!textarea) return current + value;
  const start = textarea.selectionStart ?? current.length;
  const end = textarea.selectionEnd ?? current.length;
  return current.slice(0, start) + value + current.slice(end);
};

export const TemplateCard = ({ template, variableList, onChange }) => {
  const textareaRef = useRef(null);
  return (
    <div className={`parent-tg__template${template.is_enabled ? "" : " parent-tg__template--off"}`}>
      <div className="parent-tg__template-head">
        <b>{template.label}</b>
        <label className="parent-tg__switch">
          <input type="checkbox" checked={template.is_enabled} onChange={(event) => onChange({ is_enabled: event.target.checked })} />
          {template.is_enabled ? "보내요" : "안 보내요"}
        </label>
      </div>
      <div className="parent-tg__template-body">
        <div>
          <textarea
            ref={textareaRef}
            rows={4}
            value={template.text}
            onChange={(event) => onChange({ text: event.target.value })}
          />
          <VariableChips
            variableList={variableList}
            onInsert={(value) => onChange({ text: insertAtCursor(textareaRef.current, value, template.text) })}
          />
          {unknownVariableList(template.text, variableList).length > 0 && (
            <p className="parent-tg__unknown">
              {unknownVariableList(template.text, variableList).map((key) => `{${key}}`).join(" ")} 줄은 빠지고 가요.
            </p>
          )}
          <div className="parent-tg__resets">
            {template.text !== template.default && (
              <button type="button" className="parent-tg__reset" onClick={() => onChange({ text: template.default })}>
                쓰던 알림톡 문구로
              </button>
            )}
            {template.short && template.text !== template.short && (
              <button type="button" className="parent-tg__reset" onClick={() => onChange({ text: template.short })}>
                짧은 텔레그램 문구로
              </button>
            )}
          </div>
        </div>
        <div className="parent-tg__phone">
          <span>미리보기</span>
          <div className="parent-tg__bubble">{fillPreview(`[{학원명}]\n${template.text}`, variableList)}</div>
        </div>
      </div>
    </div>
  );
};

const ParentTelegramPage = () => {
  const [data, setData] = useState(null);
  const [studentFilter, setStudentFilter] = useState("off");
  const [copied, setCopied] = useState("");
  const [templateList, setTemplateList] = useState([]);
  const [templateMessage, setTemplateMessage] = useState("");

  const apply = (next) => {
    setData(next);
    setTemplateList(next.template_list || []);
  };

  useEffect(() => {
    apiClient.get("/api/v1/echo/teacher/parent-telegram", { withAuth: true }).then((response) => apply(response.data));
  }, []);

  if (!data) return null;

  const studentList = data.student_list || [];
  const connectedList = studentList.filter((one) => one.connected);
  const waitingList = studentList.filter((one) => !one.connected);
  const shownList = studentFilter === "on" ? connectedList : waitingList;
  const variableList = data.variable_list || [];

  const copy = async (id, text) => {
    await copyText(text);
    setCopied(id);
    setTimeout(() => setCopied(""), 1500);
  };

  const saveTemplates = async () => {
    setTemplateMessage("");
    try {
      const response = await apiClient.put(
        "/api/v1/echo/teacher/parent-telegram",
        { template_list: templateList.map(({ key, text, is_enabled }) => ({ key, text, is_enabled })) },
        { withAuth: true }
      );
      apply(response.data);
      setTemplateMessage("저장했어요. 다음 알림부터 이 문구로 나가요.");
    } catch (caught) {
      setTemplateMessage(caught.response?.data?.error || "저장하지 못했어요.");
    }
  };

  const inviteText =
    "[학부모님께] 아이 학습 소식을 이제 텔레그램으로 받아 보실 수 있어요 📲\n" +
    "알림톡으로 받으신 아이 학습 페이지 위의 '텔레그램으로 받기' 를 눌러 동의하고 시작만 누르면 끝이에요.";

  return (
    <div className="parent-tg">
      <div className="parent-tg__head">
        <div>
          <div className="section-title">텔레그램</div>
        </div>
        <button type="button" className="parent-tg__kiosk" onClick={() => window.open("/attendance", "_blank")}>
          🏫 출결 화면 열기
        </button>
      </div>
      {!data.enabled && <p className="parent-tg__warn">학부모 텔레그램이 아직 준비 중이에요.</p>}

      {data.teacher_invite_url && (
        <section className="parent-tg__card parent-tg__invite">
          <div className="parent-tg__card-title">선생님 텔레그램 연결 주소</div>
          <div className="parent-tg__invite-row">
            <code>{data.teacher_invite_url}</code>
            <button type="button" onClick={() => copy("invite-url", data.teacher_invite_url)}>
              {copied === "invite-url" ? "복사했어요" : "복사"}
            </button>
          </div>
        </section>
      )}


      <section className="parent-tg__card">
        <div className="parent-tg__card-title">
          학부모 연결 <em>{connectedList.length} / {studentList.length}명</em>
        </div>
        <div className="parent-tg__tabs">
          <button type="button" aria-pressed={studentFilter === "off"} onClick={() => setStudentFilter("off")}>
            아직 안 한 집 {waitingList.length}
          </button>
          <button type="button" aria-pressed={studentFilter === "on"} onClick={() => setStudentFilter("on")}>
            연결한 집 {connectedList.length}
          </button>
        </div>
        {studentFilter === "off" && waitingList.length > 0 && (
          <div className="parent-tg__bulk">
            <button type="button" onClick={() => copy("phones", waitingList.map((one) => one.parent_phone).filter(Boolean).join("\n"))}>
              {copied === "phones" ? "복사했어요" : "번호 전체 복사 (단체문자용)"}
            </button>
            <button type="button" onClick={() => copy("invite", inviteText)}>
              {copied === "invite" ? "복사했어요" : "안내 문자 문구 복사"}
            </button>
          </div>
        )}
        <div className="parent-tg__list">
          {shownList.map((one) => (
            <div key={one.id} className="parent-tg__row">
              <b>{one.name}</b>
              <span>{one.parent_phone}</span>
              {one.connected ? (
                <em className="parent-tg__on">✅ 연결함</em>
              ) : (
                <button type="button" onClick={() => copy(`link-${one.id}`, one.consent_url)}>
                  {copied === `link-${one.id}` ? "복사했어요" : "연결 링크 복사"}
                </button>
              )}
            </div>
          ))}
          {shownList.length === 0 && <p className="parent-tg__help">해당하는 학생이 없어요.</p>}
        </div>
      </section>

      <section className="parent-tg__card">
        <div className="parent-tg__card-title">텔레그램 문구</div>
        {templateList.map((template, index) => (
          <TemplateCard
            key={template.key}
            template={template}
            variableList={data.variable_list || []}
            onChange={(patch) => setTemplateList((prev) => prev.map((one, i) => (i === index ? { ...one, ...patch } : one)))}
          />
        ))}
        <div className="parent-tg__save">
          {templateMessage && <span className="parent-tg__message">{templateMessage}</span>}
          <button type="button" className="parent-tg__primary" onClick={saveTemplates}>저장하기</button>
        </div>
      </section>
    </div>
  );
};

export default ParentTelegramPage;
